import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  ChevronRight,
  Diamond,
  Megaphone,
} from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';
import type { XkEvent, XkStock } from '@/lib/api/types';

interface ProjectThreePanelsProps {
  groupId: number;
  /** Owning brand id — drives the "Open xotskies" link to /brand/:id. */
  brandId: number;
  events: XkEvent[];
}

/**
 * PRD Xotski v2.0 §7.2 — three parallel panels on the Project Environment
 * landing: Next event · My Xotskies summary · Reporters status.
 *
 * Backend signals used:
 *   - Next event:        derived from the parent page's events list.
 *   - My Xotskies:       counts /xk-stock items scoped to the brand. Low-
 *                        stock count surfaced for inventory health glance.
 *   - Reporters:         placeholder until the Reporter View backend lands
 *                        in Phase 2 (PRD §12) — the panel reserves the slot
 *                        so the layout doesn't shift when the data arrives.
 */
export function ProjectThreePanels({ brandId, events }: ProjectThreePanelsProps) {
  const t = useT();
  const navigate = useNavigate();

  const stockQ = useQuery({
    queryKey: queryKeys.stock.all,
    queryFn: () => api.list<XkStock>('/xk-stock'),
  });
  const stockItems = stockQ.data?.items ?? [];
  const lowStock = stockItems.filter((s) => (s.initialQuantity ?? 0) === 0).length;

  const nextEvent = useMemo(() => pickNextEvent(events), [events]);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {/* Panel 1 — Next event */}
      <Card className="p-4">
        <PanelHeader
          icon={<CalendarDays className="h-4 w-4" />}
          label={t('project.panels.nextEvent.label')}
        />
        {nextEvent ? (
          <>
            <p className="mt-2 text-base font-semibold text-[var(--color-fg-primary)]">
              {nextEvent.name}
            </p>
            <p className="mt-0.5 text-xs text-[var(--color-fg-muted)]">
              {nextEvent.startDate
                ? new Date(nextEvent.startDate).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : t('project.panels.nextEvent.tbd')}
            </p>
            <button
              type="button"
              onClick={() => navigate(`/events/${nextEvent.id}`)}
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[var(--color-accent-ink)] hover:underline"
            >
              {t('project.panels.nextEvent.open')}
              <ChevronRight className="h-3 w-3" />
            </button>
          </>
        ) : (
          <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
            {t('project.panels.nextEvent.empty')}
          </p>
        )}
      </Card>

      {/* Panel 2 — My Xotskies summary */}
      <Card className="p-4">
        <PanelHeader
          icon={<Diamond className="h-4 w-4" />}
          label={t('project.panels.myXotskies.label')}
        />
        {stockQ.isLoading ? (
          <Skeleton className="mt-2 h-8 w-24" />
        ) : (
          <>
            <p className="mt-2 text-2xl font-bold tabular-nums text-[var(--color-fg-primary)]">
              {stockItems.length}
            </p>
            <p className="mt-0.5 text-xs text-[var(--color-fg-muted)]">
              {t('project.panels.myXotskies.itemsTotal')}
              {lowStock > 0 && (
                <span className="ml-2 rounded bg-[color-mix(in_srgb,var(--color-warn)_15%,transparent)] px-1.5 py-0.5 text-[10px] font-semibold uppercase text-[var(--color-warn)]">
                  {lowStock} {t('project.panels.myXotskies.lowStock')}
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={() =>
                // A la página de merch de la marca. `?tab=xotskies` era de un
                // diseño anterior: el home de la marca lo ignoraba y el enlace
                // aterrizaba en el home — el merch vive en su propia ruta.
                navigate(
                  brandId > 0
                    ? `/brand/${brandId}/xotskies`
                    : '/stock',
                )
              }
              className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[var(--color-accent-ink)] hover:underline"
            >
              {t('project.panels.myXotskies.open')}
              <ChevronRight className="h-3 w-3" />
            </button>
          </>
        )}
      </Card>

      {/* Panel 3 — Reporters status (placeholder until Phase 2 ships) */}
      <Card className="p-4">
        <PanelHeader
          icon={<Megaphone className="h-4 w-4" />}
          label={t('project.panels.reporters.label')}
        />
        <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
          {t('project.panels.reporters.comingSoon')}
        </p>
      </Card>
    </div>
  );
}

function PanelHeader({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-fg-muted)]">
      <span className="text-[var(--color-accent-ink)]">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function pickNextEvent(events: XkEvent[]): XkEvent | null {
  const now = Date.now();
  const upcoming = events
    .filter((e) => e.startDate && new Date(e.startDate).getTime() >= now)
    .sort(
      (a, b) =>
        new Date(a.startDate ?? 0).getTime() -
        new Date(b.startDate ?? 0).getTime(),
    );
  return upcoming[0] ?? null;
}
